import {
  createContext,
  useContext,
  useState,
  useCallback,
  type ReactNode,
} from 'react'
import type { Language, Translations } from './types'
import { getTranslations, getTranslation } from './i18n'
import { getStorageItem, setStorageItem, STORAGE_KEYS } from '@/lib/utils/storage'

interface I18nContextValue {
  language: Language
  setLanguage: (language: Language) => void
  t: (path: string) => string
  translations: Translations
}

const I18nContext = createContext<I18nContextValue | undefined>(undefined)

interface I18nProviderProps {
  children: ReactNode
  defaultLanguage?: Language
}

export function I18nProvider({
  children,
  defaultLanguage = 'en',
}: I18nProviderProps) {
  // Load saved language preference or use default
  const getInitialLanguage = (): Language => {
    const savedLanguage = getStorageItem(STORAGE_KEYS.preferredLanguage) as Language
    return savedLanguage || defaultLanguage
  }

  const [language, setLanguage] = useState<Language>(getInitialLanguage)
  const translations = getTranslations(language)

  const t = useCallback(
    (path: string) => {
      return getTranslation(path, language)
    },
    [language]
  )

  const handleSetLanguage = useCallback((newLanguage: Language) => {
    setLanguage(newLanguage)
    // Store preference in localStorage
    setStorageItem(STORAGE_KEYS.preferredLanguage, newLanguage)
  }, [])

  // Do not set `document.documentElement.lang` from i18n: Angular gates POST `/api/translate`
  // on `<html lang>` only. Browser “Translate page” rewrites visible text but usually leaves
  // `lang="en"` — syncing UI locale here would call the translate API while labels look
  // translated and confuse applicants. Set `<html lang="...">` explicitly when the product
  // wants non-English *document* locale (same as Angular’s `#app-parent-container`).

  return (
    <I18nContext.Provider
      value={{
        language,
        setLanguage: handleSetLanguage,
        t,
        translations,
      }}
    >
      {children}
    </I18nContext.Provider>
  )
}

/**
 * Hook to use i18n context
 */
export function useI18n() {
  const context = useContext(I18nContext)
  if (context === undefined) {
    throw new Error('useI18n must be used within an I18nProvider')
  }
  return context
}

